import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, UserRound } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { useProfileModal } from "@/contexts/ProfileModalContext";
import { fileUrl } from "@/lib/api";

/**
 * UserMenu — small controlled popover that renders a user card + two actions
 * (Profile Settings, Log out). We deliberately avoid Radix DropdownMenu here
 * because its focus-trap and portal interaction clashed with our global
 * ProfileModal — making the modal only appear after an unrelated re-render,
 * or letting clicks fall through to the "Settings" button underneath.
 *
 * `children` is the trigger element (sidebar card / top-right button).
 */
export default function UserMenu({ children, align = "end", side = "bottom" }) {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const { openProfile } = useProfileModal();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  // Close on outside click + ESC.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleProfile = () => {
    setOpen(false);
    openProfile();
  };

  const handleLogout = async () => {
    setOpen(false);
    try {
      await logout();
      toast.success(t("user_menu.logged_out"));
    } catch { /* ignore */ }
    navigate("/login", { replace: true });
  };

  const avatarUrl = user?.avatar_file_id ? fileUrl(user.avatar_file_id) : user?.picture;
  const displayName = user?.name || user?.email || "";

  // Positioning: `side` controls whether the panel sits above or below the
  // trigger; `align` controls horizontal alignment relative to the trigger.
  const sideClass = side === "top" ? "bottom-full mb-2" : "top-full mt-2";
  const alignClass = align === "end" ? "right-0" : "left-0";

  return (
    <div ref={wrapperRef} className="relative inline-block w-full">
      <div
        onClick={() => setOpen((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setOpen((v) => !v); } }}
        className="w-full"
      >
        {children}
      </div>
      {open && (
        <div
          className={`absolute z-[60] ${sideClass} ${alignClass} w-60 bg-white border border-gray-200 rounded-lg shadow-lg p-1`}
          data-testid="user-menu-content"
          role="menu"
        >
          <div className="px-3 py-2.5">
            <div className="flex items-center gap-3">
              {avatarUrl ? (
                <img src={avatarUrl} alt="" className="w-9 h-9 rounded-md object-cover" />
              ) : (
                <div className="w-9 h-9 rounded-md bg-gray-200 flex items-center justify-center text-sm text-slate-700">
                  {(displayName || "?").charAt(0).toUpperCase()}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium text-slate-900 truncate">{displayName}</div>
                {user?.email && (
                  <div className="text-[11px] text-gray-500 truncate">{user.email}</div>
                )}
              </div>
            </div>
          </div>
          <div className="my-1 h-px bg-gray-100" />
          <button
            type="button"
            data-testid="user-menu-profile"
            onClick={handleProfile}
            className="w-full flex items-center rounded-md px-3 py-2 text-[13px] text-slate-800 hover:bg-gray-100 cursor-pointer text-left"
            role="menuitem"
          >
            <UserRound className="w-4 h-4 mr-2 text-slate-600" strokeWidth={1.75} />
            {t("user_menu.profile_settings")}
          </button>
          <button
            type="button"
            data-testid="user-menu-logout"
            onClick={handleLogout}
            className="w-full flex items-center rounded-md px-3 py-2 text-[13px] text-red-600 hover:bg-red-50 cursor-pointer text-left"
            role="menuitem"
          >
            <LogOut className="w-4 h-4 mr-2" strokeWidth={1.75} />
            {t("user_menu.log_out")}
          </button>
        </div>
      )}
    </div>
  );
}
